import { useToast } from '@airship-platform/airship-patterns';
import {
  EmptyState,
  LoadingButton,
} from '@airship-platform/jam-web/components';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { useState } from 'react';
import { Link, NavLink, Outlet, useMatch, useNavigate } from 'react-router';
import {
  ProposalTemplateOption,
  useProposalTemplates,
} from '../../data-access/proposalTemplate';
import { SectionWrapper } from '../../patterns';
import ROUTE_PATHS from '../../routes/paths';
import {
  OPTION_TEMPID_PREFIX,
  OptionFormInputs,
  OptionFormWrapper,
} from '../proposal-options-manage';
import { useOptionsUpsert } from '../proposal-options-manage/optionsForm/useOptionsUpsert';
import { useApplyProposalTemplate } from './hooks/useApplyProposalTemplate';

const TEMPLATES_OPTION_FORM_ID = 'templates-option-form';

const tabItems = [
  {
    label: 'Proposal Templates',
    value: 'proposals',
  },
  {
    label: 'Option templates',
    value: 'options',
  },
  {
    label: 'Past proposals',
    value: 'history',
  },
] as const;

const buildOptionSummaries = (
  selectedOptions: {
    templateOptionIds: string[];
    appointmentProposalOptionId: string;
  }[],
  proposalTemplateOptions: ProposalTemplateOption[]
) =>
  selectedOptions
    .flatMap(({ templateOptionIds, appointmentProposalOptionId }) =>
      templateOptionIds.flatMap((proposalTemplateOptionId) =>
        proposalTemplateOptions
          .filter(
            (d) =>
              d.proposalTemplateOptionId === proposalTemplateOptionId &&
              !!d.summary
          )
          .map((d) => ({
            appointmentProposalOptionId,
            summary: d.summary,
            summaryTextPlain: d.summaryTextPlain,
          }))
      )
    )
    .reduce<Record<string, { summary: string; plainText: string }>>(
      (prev, curr) =>
        curr.summary
          ? {
              ...prev,
              [curr.appointmentProposalOptionId]: {
                summary: [
                  prev[curr.appointmentProposalOptionId]?.summary,
                  curr.summary,
                ].join(' '),
                plainText: [
                  prev[curr.appointmentProposalOptionId]?.plainText,
                  curr.summaryTextPlain,
                ].join(' '),
              },
            }
          : prev,
      {}
    );

const ProposalTemplatesView: React.FC = () => {
  const routeMatch = useMatch('*');
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState<
    (typeof tabItems)[number]['value']
  >(
    tabItems.find(
      (item) => item.value === routeMatch?.params['*']?.split('/').at(-1)
    )?.value || 'proposals'
  );
  const [isProcessingTemplates, setIsProcessingTemplates] = useState(false);

  const { proposalTemplateOptions } = useProposalTemplates();
  const { createOption, appendOptions } = useApplyProposalTemplate();
  const {
    isUpserting,
    currentOption,
    options,
    currentProposal,
    optionForm,
    upsertOptions,
    buildUpsertPayload,
  } = useOptionsUpsert();

  const selectedTemplateOptionIds = optionForm
    .watch('names')
    .flatMap((d) => d.templateOptionIds);

  if (!currentProposal) {
    return <EmptyState title="No Proposal Found" subtitle="" />;
  }

  const addToSelected = async (data: OptionFormInputs) => {
    setIsProcessingTemplates(true);

    const optionDefaultNames = data.names;

    const existingOptions = optionDefaultNames
      .filter(
        (d) =>
          !d.optionId.startsWith(OPTION_TEMPID_PREFIX) &&
          d.templateOptionIds.length
      )
      .flatMap(({ templateOptionIds, optionId }) =>
        options
          .filter((o) => o.appointmentProposalOptionId === optionId)
          .map((o) => ({ ...o, templateOptionIds }))
      );
    const newOptions = optionDefaultNames.filter((d) =>
      d.optionId.startsWith(OPTION_TEMPID_PREFIX)
    );

    const selectedOptions = !existingOptions.length
      ? await Promise.all(
          newOptions.map(({ name, templateOptionIds }, idx) =>
            createOption(currentProposal, {
              name,
              sortOrder: idx,
            }).then((newOption) => ({ ...newOption, templateOptionIds }))
          )
        )
      : await Promise.resolve(existingOptions);

    const templateSummariesMap = buildOptionSummaries(
      selectedOptions,
      proposalTemplateOptions
    );

    await appendOptions(
      currentProposal.appointmentId,
      currentProposal.appointmentProposalId,
      {
        options: selectedOptions.flatMap(
          ({ appointmentProposalOptionId, templateOptionIds }) =>
            templateOptionIds.map((proposalTemplateOptionId) => ({
              appointmentProposalOptionId,
              proposalTemplateOptionId,
            }))
        ),
        optionDefaults: selectedOptions.flatMap(
          ({ appointmentProposalOptionId }) => ({
            appointmentProposalOptionId,
            summary:
              templateSummariesMap[appointmentProposalOptionId]?.summary || '',
            summaryTextPlain:
              templateSummariesMap[appointmentProposalOptionId]?.plainText ||
              '',
          })
        ),
      }
    );

    if (selectedOptions.length) {
      showToast(`Option templates added to selected options`, 'success');
    }

    navigate(
      ROUTE_PATHS.APPOINTMENT_PATH({
        appointmentId: currentProposal.appointmentId,
        appointmentProposalId: currentProposal.appointmentProposalId,
        path: 'options',
        extraPath: selectedOptions.length
          ? selectedOptions[0].appointmentProposalOptionId
          : currentOption?.appointmentProposalOptionId,
      })
    );
    setIsProcessingTemplates(false);
  };

  const isOptionsPage = currentTab === 'options';
  const hasSelectedTemplates = selectedTemplateOptionIds.length > 0;

  const onSubmit = (data: OptionFormInputs) =>
    isOptionsPage
      ? addToSelected(data)
      : upsertOptions(buildUpsertPayload(data));

  return (
    <SectionWrapper
      maxWidth={'lg'}
      width={'100%'}
      gap={3}
      header="Proposal templates"
      subheader="Build a proposal quickly by applying a template of pre-defined options."
      actionsButtons={{
        left: {
          label: 'Previous',
          component: Link,
          to: ROUTE_PATHS.APPOINTMENT_PATH({
            appointmentId: currentProposal.appointmentId,
            appointmentProposalId: currentProposal.appointmentProposalId,
            path: 'options',
            extraPath:
              options.length && currentOption
                ? currentOption.appointmentProposalOptionId
                : 'manage',
          }),
        },
        ...((!options.length || isOptionsPage) && {
          right: {
            component: LoadingButton,
            form: TEMPLATES_OPTION_FORM_ID,
            label: isOptionsPage ? 'Add to selected' : 'Create options',
            type: 'submit',
            color:
              isOptionsPage && hasSelectedTemplates ? 'primary' : 'softGold',
            loading: isUpserting || isProcessingTemplates,
          },
        }),
      }}
    >
      <OptionFormWrapper
        id={TEMPLATES_OPTION_FORM_ID}
        onSubmit={optionForm.handleSubmit(onSubmit)}
        sx={{ gap: 2 }}
      >
        <Tabs value={currentTab}>
          {tabItems.map((item) => (
            <Tab
              component={NavLink}
              to={item.value}
              key={item.value}
              label={item.label}
              value={item.value}
              onClick={() => setCurrentTab(item.value)}
            />
          ))}
        </Tabs>

        <Outlet />
      </OptionFormWrapper>
    </SectionWrapper>
  );
};

export default ProposalTemplatesView;
